import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box, Button, Paper, Typography, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Grid, Chip, Switch, FormControlLabel,
  Alert, IconButton, Tabs, Tab, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, TablePagination, Tooltip,
} from '@mui/material';
import { Add, Edit, Delete, PlayArrow, Stop, Label } from '@mui/icons-material';
import axios from 'axios';

function TabPanel({ children, value, index }) {
  return <div hidden={value !== index}>{value === index && <Box sx={{ pt: 2 }}>{children}</Box>}</div>;
}

const COLUMNS = [
  { id: 'strategy_name', label: 'Name' },
  { id: 'strategy_type', label: 'Type' },
  { id: 'symbol', label: 'Symbol' },
  { id: 'exchange', label: 'Exchange' },
  { id: 'is_active', label: 'Status' },
  { id: 'modes', label: 'Modes', disableSort: true },
  { id: 'actions', label: '', disableSort: true },
];

const PAGE_OPTS = [10, 20, 50, 100, -1];

export default function StrategyManager({ apiUrl }) {
  const [strategies, setStrategies] = useState([]);
  const [total, setTotal] = useState(0);
  const [groups, setGroups] = useState([]);
  const [activeGroupId, setActiveGroupId] = useState('all');

  const [openDialog, setOpenDialog] = useState(false);
  const [selectedStrategy, setSelectedStrategy] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [formTab, setFormTab] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('created_at');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  // New-group inline creation
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupError, setNewGroupError] = useState('');
  const [showNewGroupInput, setShowNewGroupInput] = useState(false);
  const newGroupInputRef = useRef(null);

  const [formData, setFormData] = useState({
    strategy_name: '', strategy_type: 'tradingview', description: '',
    exchange: 'NSE', symbol: '', paper_trade_enabled: true, real_trade_enabled: false,
    group_id: '', tags: [],
  });
  const [settingsData, setSettingsData] = useState({
    take_profit: 100, stop_loss: 50, trailing_stop_loss_trigger: 20,
    trailing_stop_loss_move: 10, lot_size: 1, max_positions: 1, threshold_points: 15,
  });
  const [tagInput, setTagInput] = useState('');

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${apiUrl}/strategies/groups/`, { headers });
      setGroups(res.data || []);
    } catch { /* silent */ }
  }, [apiUrl]);

  const fetchStrategies = useCallback(async () => {
    try {
      const params = {
        skip: page * rowsPerPage,
        limit: rowsPerPage === -1 ? 1000 : rowsPerPage,
        sort_by: orderBy,
        sort_order: order,
        ...(activeGroupId !== 'all' ? { group_id: activeGroupId } : {}),
      };
      const res = await axios.get(`${apiUrl}/strategies/`, { headers, params });
      setStrategies(res.data.items || []);
      setTotal(res.data.total || 0);
    } catch { setError('Failed to fetch strategies'); }
  }, [apiUrl, page, rowsPerPage, orderBy, order, activeGroupId]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);
  useEffect(() => { fetchStrategies(); }, [fetchStrategies]);

  const handleSort = (col) => {
    const isAsc = orderBy === col && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(col);
    setPage(0);
  };

  const handleGroupTabChange = (_, value) => {
    if (value === '__new__') return;
    setActiveGroupId(value);
    setPage(0);
  };

  // ── Tag helpers ────────────────────────────────────────────────────────────
  const commitTag = () => {
    const t = tagInput.trim().replace(/,+$/, '');
    if (t && !formData.tags.includes(t)) {
      setFormData(f => ({ ...f, tags: [...f.tags, t] }));
    }
    setTagInput('');
  };

  const removeTag = (tag) => {
    setFormData(f => ({ ...f, tags: f.tags.filter(t => t !== tag) }));
  };

  const handleTagKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      commitTag();
    } else if (e.key === 'Backspace' && !tagInput && formData.tags.length > 0) {
      setFormData(f => ({ ...f, tags: f.tags.slice(0, -1) }));
    }
  };

  // ── Group creation ─────────────────────────────────────────────────────────
  const handleCreateGroup = async () => {
    const name = newGroupName.trim();
    if (!name) return;
    try {
      await axios.post(`${apiUrl}/strategies/groups/`, { name }, { headers });
      setNewGroupName(''); setShowNewGroupInput(false); setNewGroupError('');
      await fetchGroups();
    } catch (err) {
      setNewGroupError(err.response?.data?.detail || 'Failed to create group');
    }
  };

  const handleDeleteGroup = async (groupId, e) => {
    e.stopPropagation();
    if (!window.confirm('Delete this group? Its strategies will move to Default.')) return;
    try {
      await axios.delete(`${apiUrl}/strategies/groups/${groupId}`, { headers });
      if (activeGroupId === groupId) setActiveGroupId('all');
      await fetchGroups();
      await fetchStrategies();
    } catch { setError('Failed to delete group'); }
  };

  // ── Strategy CRUD ──────────────────────────────────────────────────────────
  const handleSaveStrategy = async () => {
    setLoading(true); setError(''); setSuccess('');
    try {
      if (editMode && selectedStrategy) {
        await axios.put(`${apiUrl}/strategies/${selectedStrategy.id}`, {
          strategy_name: formData.strategy_name,
          strategy_type: formData.strategy_type,
          description: formData.description,
          exchange: formData.exchange,
          symbol: formData.symbol,
          paper_trade_enabled: formData.paper_trade_enabled,
          real_trade_enabled: formData.real_trade_enabled,
          group_id: formData.group_id || undefined,
          tags: formData.tags,
        }, { headers });
        await axios.put(`${apiUrl}/strategies/${selectedStrategy.id}/settings`, settingsData, { headers });
        setSuccess('Strategy updated!');
      } else {
        const res = await axios.post(`${apiUrl}/strategies/`, {
          ...formData,
          group_id: formData.group_id || undefined,
        }, { headers });
        await axios.post(`${apiUrl}/strategies/settings`, { strategy_id: res.data.id, ...settingsData }, { headers });
        setSuccess('Strategy created!');
      }
      setOpenDialog(false); fetchStrategies(); resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to save');
    } finally { setLoading(false); }
  };

  const handleEditStrategy = async (strategy) => {
    setEditMode(true); setSelectedStrategy(strategy);
    setFormData({
      strategy_name: strategy.strategy_name,
      strategy_type: strategy.strategy_type,
      description: strategy.description || '',
      exchange: strategy.exchange,
      symbol: strategy.symbol,
      paper_trade_enabled: strategy.paper_trade_enabled,
      real_trade_enabled: strategy.real_trade_enabled,
      group_id: strategy.group_id || '',
      tags: strategy.tags || [],
    });
    try {
      const res = await axios.get(`${apiUrl}/strategies/${strategy.id}/settings`, { headers });
      if (res.data) setSettingsData({
        take_profit: res.data.take_profit || 100, stop_loss: res.data.stop_loss || 50,
        trailing_stop_loss_trigger: res.data.trailing_stop_loss_trigger || 20,
        trailing_stop_loss_move: res.data.trailing_stop_loss_move || 10,
        lot_size: res.data.lot_size || 1, max_positions: res.data.max_positions || 1,
        threshold_points: res.data.threshold_points || 15,
      });
    } catch {}
    setOpenDialog(true);
  };

  const handleToggle = async (id, isActive) => {
    try {
      await axios.put(`${apiUrl}/strategies/${id}`, { is_active: !isActive }, { headers });
      fetchStrategies();
    } catch { setError('Failed to toggle'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this strategy?')) return;
    try {
      await axios.delete(`${apiUrl}/strategies/${id}`, { headers });
      fetchStrategies(); setSuccess('Strategy deleted');
    } catch { setError('Failed to delete'); }
  };

  const resetForm = () => {
    setFormData({
      strategy_name: '', strategy_type: 'tradingview', description: '',
      exchange: 'NSE', symbol: '', paper_trade_enabled: true, real_trade_enabled: false,
      group_id: '', tags: [],
    });
    setSettingsData({ take_profit: 100, stop_loss: 50, trailing_stop_loss_trigger: 20, trailing_stop_loss_move: 10, lot_size: 1, max_positions: 1, threshold_points: 15 });
    setTagInput(''); setEditMode(false); setSelectedStrategy(null); setFormTab(0);
  };

  const activeGroupLabel = activeGroupId === 'all'
    ? 'All Strategies'
    : (groups.find(g => g.id === activeGroupId)?.name || 'Strategies');

  return (
    <Box data-testid="strategy-manager">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
        <Typography variant="h5">Strategy Management</Typography>
        <Button variant="contained" size="small" startIcon={<Add />}
          onClick={() => { resetForm(); setOpenDialog(true); }}
          data-testid="create-strategy-btn">Create Strategy</Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 1 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Group tabs */}
      <Paper variant="outlined" sx={{ mb: 1.5, px: 1 }}>
        <Box display="flex" alignItems="center" gap={1}>
          <Tabs
            value={activeGroupId}
            onChange={handleGroupTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ flex: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 0, fontSize: '0.78rem' } }}
          >
            <Tab value="all" label="All" />
            {groups.map(g => (
              <Tab
                key={g.id}
                value={g.id}
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    {g.name}
                    {g.name !== 'Default' && (
                      <Box
                        component="span"
                        onClick={(e) => handleDeleteGroup(g.id, e)}
                        sx={{ ml: 0.5, fontSize: '0.7rem', opacity: 0.5, '&:hover': { opacity: 1 }, cursor: 'pointer', lineHeight: 1 }}
                      >✕</Box>
                    )}
                  </Box>
                }
              />
            ))}
          </Tabs>

          {/* New group creation */}
          {showNewGroupInput ? (
            <Box display="flex" alignItems="center" gap={0.5} sx={{ py: 0.5 }}>
              <TextField
                size="small" autoFocus inputRef={newGroupInputRef}
                value={newGroupName} placeholder="Group name"
                onChange={(e) => setNewGroupName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreateGroup(); if (e.key === 'Escape') { setShowNewGroupInput(false); setNewGroupName(''); setNewGroupError(''); } }}
                error={!!newGroupError} helperText={newGroupError}
                sx={{ width: 150, '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8rem' } }}
              />
              <Button size="small" variant="contained" sx={{ py: 0.4, fontSize: '0.75rem' }} onClick={handleCreateGroup}>Add</Button>
              <Button size="small" sx={{ py: 0.4, fontSize: '0.75rem' }} onClick={() => { setShowNewGroupInput(false); setNewGroupName(''); setNewGroupError(''); }}>Cancel</Button>
            </Box>
          ) : (
            <Tooltip title="New group">
              <IconButton size="small" onClick={() => setShowNewGroupInput(true)}>
                <Add sx={{ fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Paper>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {COLUMNS.map((col) => (
                  <TableCell key={col.id} sortDirection={orderBy === col.id ? order : false}>
                    {col.disableSort ? col.label : (
                      <TableSortLabel active={orderBy === col.id} direction={orderBy === col.id ? order : 'asc'}
                        onClick={() => handleSort(col.id)}>{col.label}</TableSortLabel>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {strategies.length === 0 ? (
                <TableRow><TableCell colSpan={7}>
                  <Alert severity="info">No strategies{activeGroupId !== 'all' ? ' in this group' : ''}. Create one to start.</Alert>
                </TableCell></TableRow>
              ) : strategies.map((s) => (
                <TableRow key={s.id} data-testid={`strategy-row-${s.id}`} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={700}>{s.strategy_name}</Typography>
                    {s.description && <Typography variant="caption" color="text.secondary" display="block">{s.description}</Typography>}
                    {s.tags && s.tags.length > 0 && (
                      <Box display="flex" gap={0.4} flexWrap="wrap" mt={0.3}>
                        {s.tags.map(tag => (
                          <Chip key={tag} label={tag} size="small"
                            icon={<Label sx={{ fontSize: '0.7rem !important' }} />}
                            sx={{ height: 16, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.5 } }}
                            variant="outlined" color="info" />
                        ))}
                      </Box>
                    )}
                    {s.group_name && activeGroupId === 'all' && (
                      <Typography variant="caption" color="text.disabled" display="block">{s.group_name}</Typography>
                    )}
                  </TableCell>
                  <TableCell><Chip label={s.strategy_type.toUpperCase()} size="small" variant="outlined" /></TableCell>
                  <TableCell><Typography variant="body2" fontWeight={600}>{s.symbol}</Typography></TableCell>
                  <TableCell>{s.exchange}</TableCell>
                  <TableCell>
                    <Chip label={s.is_active ? 'Active' : 'Inactive'} size="small"
                      color={s.is_active ? 'success' : 'default'} />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5}>
                      {s.paper_trade_enabled && <Chip label="Paper" size="small" variant="outlined" />}
                      {s.real_trade_enabled && <Chip label="Real" size="small" color="primary" />}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0}>
                      <Tooltip title={s.is_active ? 'Deactivate' : 'Activate'}>
                        <IconButton size="small" onClick={() => handleToggle(s.id, s.is_active)}
                          color={s.is_active ? 'error' : 'success'}>
                          {s.is_active ? <Stop sx={{ fontSize: 18 }} /> : <PlayArrow sx={{ fontSize: 18 }} />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit">
                        <IconButton size="small" color="primary" onClick={() => handleEditStrategy(s)}>
                          <Edit sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => handleDelete(s.id)}>
                          <Delete sx={{ fontSize: 18 }} />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={PAGE_OPTS.map(v => v === -1 ? { value: -1, label: 'All' } : v)}
          component="div"
          count={total}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setPage(0); }}
          data-testid="strategy-pagination"
        />
      </Paper>

      {/* Create / Edit Dialog */}
      <Dialog open={openDialog} onClose={() => { setOpenDialog(false); resetForm(); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ py: 1.5 }}>{editMode ? 'Edit Strategy' : 'Create Strategy'}</DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}
          <Tabs value={formTab} onChange={(e, v) => setFormTab(v)} sx={{ mb: 1 }}>
            <Tab label="Basic" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }} />
            <Tab label="Settings" sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.8rem' }} />
          </Tabs>

          <TabPanel value={formTab} index={0}>
            <Grid container spacing={1.5}>
              <Grid item xs={12}><TextField fullWidth size="small" label="Strategy Name" value={formData.strategy_name}
                onChange={(e) => setFormData({ ...formData, strategy_name: e.target.value })} required
                data-testid="strategy-name-input" /></Grid>

              <Grid item xs={6}>
                <FormControl fullWidth size="small"><InputLabel>Type</InputLabel>
                  <Select value={formData.strategy_type} label="Type"
                    onChange={(e) => setFormData({ ...formData, strategy_type: e.target.value })}
                    data-testid="strategy-type-select">
                    <MenuItem value="tradingview">TradingView</MenuItem>
                    <MenuItem value="chartink">Chartink</MenuItem>
                    <MenuItem value="arbitrage">Arbitrage</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={6}>
                <FormControl fullWidth size="small"><InputLabel>Exchange</InputLabel>
                  <Select value={formData.exchange} label="Exchange"
                    onChange={(e) => setFormData({ ...formData, exchange: e.target.value })}
                    data-testid="strategy-exchange-select">
                    {['NSE', 'BSE', 'NFO', 'MCX'].map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}><TextField fullWidth size="small" label="Symbol" value={formData.symbol}
                onChange={(e) => setFormData({ ...formData, symbol: e.target.value })} required
                data-testid="strategy-symbol-input" /></Grid>

              <Grid item xs={12}><TextField fullWidth size="small" label="Description" value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })} multiline rows={2} /></Grid>

              {/* Group selector */}
              <Grid item xs={12}>
                <FormControl fullWidth size="small"><InputLabel>Group</InputLabel>
                  <Select value={formData.group_id} label="Group"
                    onChange={(e) => setFormData({ ...formData, group_id: e.target.value })}>
                    <MenuItem value=""><em>Default (auto-assign)</em></MenuItem>
                    {groups.map(g => <MenuItem key={g.id} value={g.id}>{g.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>

              {/* Tags chip input */}
              <Grid item xs={12}>
                <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>Tags</Typography>
                <Box
                  display="flex" flexWrap="wrap" gap={0.5} alignItems="center"
                  onClick={() => document.getElementById('tag-input-field').focus()}
                  sx={{
                    border: '1px solid', borderColor: 'divider', borderRadius: 1,
                    p: 0.75, minHeight: 38, cursor: 'text',
                    '&:focus-within': { borderColor: 'primary.main', borderWidth: 2 },
                  }}
                >
                  {formData.tags.map(tag => (
                    <Chip key={tag} label={tag} size="small" onDelete={() => removeTag(tag)}
                      sx={{ height: 22, fontSize: '0.75rem' }} />
                  ))}
                  <input
                    id="tag-input-field"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagKeyDown}
                    onBlur={commitTag}
                    placeholder={formData.tags.length === 0 ? 'Type a tag and press Enter…' : ''}
                    style={{ border: 'none', outline: 'none', flex: 1, minWidth: 120, background: 'transparent', fontSize: '0.85rem', color: 'inherit' }}
                  />
                </Box>
                <Typography variant="caption" color="text.disabled">Press Enter or comma to add a tag</Typography>
              </Grid>

              <Grid item xs={6}>
                <FormControlLabel control={<Switch size="small" checked={formData.paper_trade_enabled}
                  onChange={(e) => setFormData({ ...formData, paper_trade_enabled: e.target.checked })} />}
                  label={<Typography variant="body2">Paper Trading</Typography>} />
              </Grid>
              <Grid item xs={6}>
                <FormControlLabel control={<Switch size="small" checked={formData.real_trade_enabled}
                  onChange={(e) => setFormData({ ...formData, real_trade_enabled: e.target.checked })} />}
                  label={<Typography variant="body2">Real Trading</Typography>} />
              </Grid>
            </Grid>
          </TabPanel>

          <TabPanel value={formTab} index={1}>
            <Grid container spacing={1.5}>
              {[
                { key: 'take_profit', label: 'Take Profit (pts)' },
                { key: 'stop_loss', label: 'Stop Loss (pts)' },
                { key: 'trailing_stop_loss_trigger', label: 'TSL Trigger (pts)' },
                { key: 'trailing_stop_loss_move', label: 'TSL Move (pts)' },
                { key: 'lot_size', label: 'Lot Size', int: true },
                { key: 'max_positions', label: 'Max Positions', int: true },
              ].map(({ key, label, int }) => (
                <Grid item xs={6} key={key}>
                  <TextField fullWidth size="small" type="number" label={label} value={settingsData[key]}
                    onChange={(e) => setSettingsData({ ...settingsData, [key]: int ? parseInt(e.target.value) : parseFloat(e.target.value) })} />
                </Grid>
              ))}
              {formData.strategy_type === 'arbitrage' && (
                <Grid item xs={12}><TextField fullWidth size="small" type="number" label="Arbitrage Threshold (pts)"
                  value={settingsData.threshold_points}
                  onChange={(e) => setSettingsData({ ...settingsData, threshold_points: parseFloat(e.target.value) })}
                  helperText="Min price difference to trigger" /></Grid>
              )}
            </Grid>
          </TabPanel>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setOpenDialog(false); resetForm(); }}>Cancel</Button>
          <Button onClick={handleSaveStrategy} variant="contained"
            disabled={loading || !formData.strategy_name || !formData.symbol}
            data-testid="save-strategy-btn">
            {loading ? 'Saving...' : editMode ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
